import { parse } from 'regexparam';
/**
 * Converts a path to a URL.
 * If the path is a full URL, it returns a URL object.
 * If the path is a path, it returns a URL object with the origin of the current window.
 * If the path is a query string, it returns a URL object with the href of the current window.
 * @param path - The path to convert.
 * @returns A URL object.
 */
export function toURL(path) {
    try {
        return new URL(path);
    }
    catch {
        return new URL(path, path.startsWith('?') ? `${window.location.href}${path}` : window.location.origin);
    }
}
/**
 * Converts a path to a relative path.
 * @param path - The path to convert.
 * @param base - The base path.
 * @returns A relative path.
 */
export function relativePath(path, base) {
    if (!base || base === '/') {
        return path;
    }
    return path.startsWith(base) ? path.slice(base.length) : path;
}
/**
 * Converts a path to an absolute path.
 * @param path - The path to convert. Something like `/search/new`
 * @param base - The base path. Something like `/sidecar
 * @param search - The search query string. Something like `?q=test`
 * @param hash - The hash fragment. Something like `#audit-log`
 * @returns An absolute path.
 */
export function absolutePath(path, base, search, hash) {
    if (!base || base === '/' || path.startsWith(base)) {
        return `${path}${search ?? ''}${hash ?? ''}`;
    }
    return `${base}${path}${search ?? ''}${hash ?? ''}`.replace(/\/+$/, '');
}
// Route definitions compile to the same regex every time, so compile each definition once.
// Keyed by the route string, not the routes array, because callers often pass fresh arrays.
// Only `^` routes and the default parser are cached: custom parsers may be impure or return
// stateful `g`/`y` regexes. Callers pass route definitions, not pathnames, so this stays bounded.
const compiledRoutes = new Map();
function compileRoute(route, parser) {
    const isRegexRoute = route.startsWith('^');
    if (!isRegexRoute && parser !== parse)
        return parser(route);
    let compiled = compiledRoutes.get(route);
    if (compiled === undefined) {
        compiled = isRegexRoute ? { pattern: new RegExp(route), keys: [] } : parse(route);
        compiledRoutes.set(route, compiled);
    }
    return compiled;
}
export function matchRoute(routes, pathname, parser = parse) {
    for (const route of routes) {
        const { pattern, keys } = compileRoute(route, parser);
        if (route.startsWith('^')) {
            const result = pattern.exec(pathname);
            const match = result !== null;
            if (!match)
                continue;
            return {
                pattern,
                route,
                match,
                params: Object.fromEntries(Object.entries(result?.groups ?? {}).map(([key, value]) => [key, value ?? undefined])),
            };
        }
        const result = pattern.exec(pathname);
        // Only custom parsers can return stateful regexes that need the original exec/test sequence.
        const match = parser === parse ? result !== null : pattern.test(pathname);
        if (!match)
            continue;
        const [_, ...matches] = result ?? [];
        return {
            pattern,
            route,
            match,
            params: Object.fromEntries(keys.map((key, i) => [key, matches?.[i]])),
        };
    }
    return undefined;
}
//# sourceMappingURL=UrlUtils.js.map